import { triniumApi } from '@api/Trinium/slice';
import { DispatchLeg, upsertLegsInDispatchCache } from '@api/Trinium/Endpoints/Dispatch/types';
import {
  SendDataResponse,
  SendPudlReceiptRequest,
  SendPudlReceiptsBatchRequest,
  SendPudlReceiptsBatchResponse,
  WarmupSendDataRequest
} from './types';
import { patchLegInConsignmentCache, updateLegApiCache } from '../Leg';

/** Flags the legs as having had their receipt sent in every cache that displays the receipt indicator. */
function markReceiptsSent(legs: Pick<DispatchLeg, 'legId' | 'csnId'>[]) {
  upsertLegsInDispatchCache(legs.map((leg) => ({ legId: leg.legId, puDelvReceiptSent: true })));

  legs.forEach((leg) => {
    updateLegApiCache({
      id: leg.legId,
      puDelvReceiptSent: true
    });

    // Patch the Consignment cache so the receipt icon on the Order Details Legs tab refreshes without a re-fetch.
    patchLegInConsignmentCache(leg.legId, leg.csnId, {
      puDelvReceiptSent: true
    });
  });
}

export const pudlReceiptSlice = triniumApi.injectEndpoints({
  endpoints: (builder) => ({
    warmupPudlReceipt: builder.mutation<SendDataResponse, WarmupSendDataRequest>({
      query: ({ terminalNumber, legId, includeAllLegs, includeCustInfo }) => ({
        url: `${terminalNumber}/pickupdeliveryreceipts/${String(legId)}/data?includeAllLegs=${String(includeAllLegs)}&includeCustInfo=${String(includeCustInfo)}`,
        method: 'GET'
      }),
      transformResponse: (response: SendDataResponse) => ({
        ...response,
        textBody: response.textBody || ''
      })
    }),
    // eslint-disable-next-line @typescript-eslint/no-invalid-void-type
    sendPudlReceipt: builder.mutation<void, SendPudlReceiptRequest>({
      query: ({ type, ...params }) => ({
        url: `${params.terminalNumber}/pickupdeliveryreceipts/${String(params.legId)}`,
        method: 'PUT',
        body: {
          action: type.action,
          data: type.data
        }
      }),
      async onQueryStarted(params, { queryFulfilled }) {
        await queryFulfilled;
        markReceiptsSent([params]);
      }
    }),
    sendPudlReceiptsBatch: builder.mutation<SendPudlReceiptsBatchResponse, SendPudlReceiptsBatchRequest>({
      query: ({ terminalNumber, legs, data }) => ({
        url: `${terminalNumber}/pickupdeliveryreceipts/${String(legs[0]?.legId)}`,
        method: 'PUT',
        body: {
          action: 'SendReceiptsBatch',
          data: {
            ...data,
            legIds: legs.map((leg) => leg.legId)
          }
        }
      }),
      async onQueryStarted({ legs }, { queryFulfilled }) {
        const { data: result } = await queryFulfilled;

        // Only legs whose assigned driver actually received the receipt are flagged; copies do not count.
        const sentLegIds = new Set(result.receipts.filter((receipt) => receipt.emailed || receipt.texted).map((receipt) => receipt.legId));
        markReceiptsSent(legs.filter((leg) => sentLegIds.has(leg.legId)));
      }
    })
  })
});

export const {
  useWarmupPudlReceiptMutation,
  useSendPudlReceiptMutation,
  useSendPudlReceiptsBatchMutation
} = pudlReceiptSlice;
